// dependencies
import React, { useEffect } from "react";

import { IconDefinition, faAngleDown } from "@fortawesome/pro-regular-svg-icons";

import Icon from "../Icon";
// components
import Text from "../Text";
// styles
import css from "./VerticalNavBar.module.scss";

export interface NavItem {
	title: string;
	icon?: IconDefinition;
	path?: string;
	children?: NavItem[];
	/** Renders a divider above this item; ignored on the first item so none floats above the nav. */
	divider?: "before";
	/** Label for items that navigate outside administration, where the default label would misread. */
	ariaLabel?: string;
}

interface BaseProps {
	item: NavItem;
	isGrouped?: boolean;
	isSidebarOpen: boolean;
	openNavs: string[];
	setOpenNavs: (navTitles: string[]) => void;
}

const SidebarItem = (props: BaseProps) => {
	const { item, isGrouped = false, isSidebarOpen, openNavs, setOpenNavs } = props;

	let isActiveNav = item.path === window.location.pathname;

	if (item.children) {
		isActiveNav = item.children.some((child) => child.path === window.location.pathname);
	}

	useEffect(() => {
		if (!isSidebarOpen) {
			setOpenNavs([]);
		}
	}, [isActiveNav, isSidebarOpen]);

	const handleSetOpenNav = (navTitle: string) => {
		if (openNavs.includes(navTitle)) {
			setOpenNavs([]);
		} else {
			isGrouped ? setOpenNavs([...openNavs, navTitle]) : setOpenNavs([navTitle]);
		}
	};

	if (item.children) {
		return (
			<>
				<div
					className={`${css.sidebarItem} ${css.group} ${openNavs.includes(item.title) ? css.open : ""} ${
						isActiveNav ? css.active : ""
					}`}
					onClick={() => handleSetOpenNav(item.title)}
				>
					<div className={`${css.sidebarTitle}`}>
						<div>
							{item.icon ? <Icon className={css.navIcon} icon={item.icon} /> : null}
							<Text className={css.navItemLabel} size="paragraph-3" weight="semi-bold">
								{item.title}
							</Text>
						</div>
						<Icon icon={faAngleDown} className={css.toggleBtn} />
					</div>
				</div>
				<div className={`${css.sidebarContent} ${openNavs.includes(item.title) ? css.open : ""}`}>
					{item.children.map((child, index) => (
						<SidebarItem
							key={index}
							item={child}
							isGrouped={true}
							isSidebarOpen={isSidebarOpen}
							openNavs={openNavs}
							setOpenNavs={setOpenNavs}
						/>
					))}
				</div>
			</>
		);
	} else {
		return (
			<a
				href={item.path || "#"}
				aria-label={item.ariaLabel ?? `${item.title} administration page`}
				className={`${css.sidebarItem} ${isActiveNav ? css.active : ""} ${isGrouped ? css.grouped : ""}`}
			>
				{item.icon ? <Icon className={css.navIcon} icon={item.icon} /> : null}
				<Text className={css.navItemLabel} size="paragraph-3" weight={isGrouped ? "regular" : "semi-bold"}>
					{item.title}
				</Text>
			</a>
		);
	}
};

export default SidebarItem;
